import { ErrorBoundary } from "@sentry/react";
import React, { useContext, useEffect, useState } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { setSpecificParameter } from "../../actions";
import {
  cpoOperationalParams,
  getCpoParams,
  getPmoQueryParams,
  getPurchaseFunnelParameters,
  getShowTouchPoints,
  midasApiParamsToObject,
  msrpApiParamsToObject,
  oceNewParameter,
  pmoParamsIncludeAvgModels,
} from "../../api";
import { brandPerformanceApiParamsToObject } from "../../api/brandPerformanceApi";
import { getParams as getExecutiveSummaryProductPerformanceParams } from "../../api/executiveSummaryProductPerformanceApi";
import { getParams as getExecutiveSummaryDisParams } from "../../api/executiveSummaryWebsiteApi";
import { getParams as getMaketingPerformanceSummaryParams } from "../../api/JourneyExcellenceMarketPerformanceSummary";
import { journeyExcellenceApiParamsToObject } from "../../api/journeyExcellenceApi";
import { getQueryParams as getQueryParamsProductPerformance } from "../../api/productPerformanceApi";
import { postDataV2 } from "../../api/utils";
import { getVOCParams } from "../../api/vocApi";
import "../../assets/styles/component/pageContainer.scss";
import { DashboardBannerContext, DocumentationContext, FilterContext, ThemeContext } from "../../context";
import { JourneyExcellenceContext } from "../../pages/Journey Excellence/DeepDive/context/JourneyExcellenceContext";
import { getStartEnd } from "../../utils/dateFunctions";
import { isExportDownload, triggerExportDownload } from "../../utils/downloadExport";
import { eventTracking, MixpanelEvents } from "../../utils/userTracking";
import { ErrorMsg } from "../AppMessages";
import { DashboardAlertWidget } from "../DashboardAlertWidget/DashboardAlertWidget";
import { Documentation } from "../Documentation/Documentation";
import MenuOptions from "../MenuOptions/MenuOptions";
import { handlePDFDownload } from "../MenuOptions/subs";
import { SaveHomePage } from "../Modals";
import { MetricBreadcrumb } from "./subs";
import { getParams } from "../../api/dmeoApi";
import { getParams as getQuickVocSalesParams } from "../../api/quickVocSalesApi";
import { getParams as getQuickVocAfterSalesParams } from "../../api/quickVocAfterSales";
import { getParams as getQuickVocCecParams } from "../../api/quickVocCecApi";
import { getParams as getExecutiveSummaryProductPerformanceDeepDiveParams } from "../../api/executiveSummaryProductPerformanceDeepDive";
import { ecommerceApiParamsToObject } from "../../api/ecommerce";
import { apiParamsToObjectUseOwnership } from "../../api/useOwnershipApi";
import { exportDashboardAnalysis } from "../../api/analysisApi";
import { useDashboardRegistry } from "../../hooks/react-query/dashboards/useDashboardRegistry";

interface Props {
  filter: JSX.Element;
  children: JSX.Element;
  lastDataRefresh: string;
  dataDocumentation: string;
  showLastDataRefreshAlert?: boolean;
  hideDownloadPdf?: boolean;
  isMTM?: boolean;
  pageTitle: JSX.Element;
  extraClass?: string;
  dataAvailableTerm?: string;
  // The brand_performance ANALYSIS_REGISTRY page/export keys for the CSV export button on
  // whichever brand_performance tab is currently active (see BrandPerformanceDashboard.tsx's own
  // customCSVExport map). Two of the 22 tabs (yearly/RV table-for-download) have no registered
  // page/export yet - brandPerformanceCustomCSVUrl stays as the fallback for those specifically.
  brandPerformancePage?: string;
  brandPerformanceExport?: string;
  brandPerformanceCustomCSVUrl?: string;
  // The live _dashboard.key this page corresponds to (GET /dashboards, via useDashboardRegistry) -
  // resolves to the numeric dashboard_id every /analysis/{dashboard_id}/export call below needs.
  // Omitted (or not yet present in the registry, e.g. product_performance/msrp - see
  // useDashboardRegistry callers' own comments) means CSV export handlers fall back to their
  // still-existing bespoke per-dashboard endpoints instead of the new generic one.
  dashboardKey?: string;
}

export const DefaultPageContent = (props: Props): JSX.Element => {
  const {
    children,
    extraClass,
    lastDataRefresh,
    filter,
    dataDocumentation,
    showLastDataRefreshAlert,
    hideDownloadPdf,
    pageTitle,
    dataAvailableTerm,
    brandPerformancePage,
    brandPerformanceExport,
    brandPerformanceCustomCSVUrl,
    dashboardKey,
  } = props;

  const { dashboards } = useDashboardRegistry();
  const dashboardId = dashboards.find((dashboard) => dashboard.key === dashboardKey)?.id;

  const { setShowMobileFilter } = useContext(FilterContext);
  const { detailText, bannerText, showBanner, setBannerHovered } = useContext(DashboardBannerContext);
  const { theme } = useContext(ThemeContext);

  const [metricActive] = useState<boolean>(false);
  const [showSaveHomePage, setShowSaveHomePage] = useState<boolean>(false);
  const [path] = useState(window.location.href);

  useEffect(() => {
    // hide and show metric breadcrumb
    const metricButtonId = "metric_button";
    const metricBreadcrumbElement = document.getElementById(metricButtonId);

    const shownClass = "isVisible";
    if (metricActive) metricBreadcrumbElement?.classList.add(shownClass);
    else metricBreadcrumbElement?.classList.remove(shownClass);
  }, [metricActive]);
  const dateRangeParamValue = useSelector((state: RootStateOrAny) => state.parameters.date_range);
  const appStoreParamValue = useSelector((state: RootStateOrAny) => state.parameters.app_store);
  const marketParamValue = useSelector((state: RootStateOrAny) => state.parameters.market);
  const experienceRatingParamValue = useSelector((state: RootStateOrAny) => state.parameters.experience_rating);
  const googleRatingParamValue = useSelector((state: RootStateOrAny) => state.parameters.google_rating);
  const topicParamValue = useSelector((state: RootStateOrAny) => state.parameters.topic);
  const touchpointParamValue = useSelector((state: RootStateOrAny) => state.parameters.touchpoint);
  const {
    digitally_influenced_sales_cumulative,
    private_sales_breakdown_cumulative,
    digital_leads_by_source_digitally_influenced_leads,
    sales_breakdown_by_source_digitally_influenced_sales,
  } = useSelector((state: RootStateOrAny) => state.parameters);
  const vocCsvExperienceRating = useSelector((state: RootStateOrAny) => state.voc.experience_rating);

  const [hideAppRatingsBanner, setHideAppRatingsBanner] = useState<boolean>(true);

  useEffect(() => {
    const dates = dateRangeParamValue.split(",");
    const banner_dates = ["December 2021", "January 2022", "February 2022", "March 2022"];
    let isInRange = false;
    for (const date of banner_dates) {
      if (dates.includes(date)) {
        isInRange = true;
        break;
      }
    }
    isInRange && !appStoreParamValue.includes("iOS") ? setHideAppRatingsBanner(false) : setHideAppRatingsBanner(true);
  }, [dateRangeParamValue, appStoreParamValue]);

  const handleSaveHomePageClick = () => {
    setShowSaveHomePage(true);
  };

  const handleCloseSaveHomePage = () => {
    setShowSaveHomePage(false);
  };

  const dispatch = useDispatch();
  const verts = useSelector((state: RootStateOrAny) => state.parameters.verticalScroll);
  const handleScroll = (event: { currentTarget: any }) => {
    const pagePath = location?.pathname?.split("/")[2]?.replace(/_/g, "-");
    if (pagePath === "mtp-category") {
      const verticalScroll2 = document.getElementById("container");
      if (verticalScroll2 && verticalScroll2?.scrollTop > 20 && !verts) {
        dispatch(setSpecificParameter("verticalScroll", true));
      }
      if (verticalScroll2 && verticalScroll2?.scrollTop < 20 && verts) {
        dispatch(setSpecificParameter("verticalScroll", false));
      }
    }
  };

  // The frontend's own /midas/{segment} route segments match ANALYSIS_REGISTRY["midas"]'s page
  // keys 1:1, except fmi_by_model - the registry calls that page media_fmi_by_model.
  const MIDAS_PAGE_KEY_MAP: Record<string, string> = { fmi_by_model: "media_fmi_by_model" };
  const MIDAS_TOUCHPOINT_PAGES = ["strategic_dealer_support", "strategic_ce", "fixed"];

  const handleMidasCSVDownload = () => {
    const searchParams = window.location.search;
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 1000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "Fmi run rate" });

    const pathSegment = location.pathname.split("/")[2];
    const page = MIDAS_PAGE_KEY_MAP[pathSegment] ?? pathSegment;
    const filters: Record<string, any> = { ...midasApiParamsToObject(searchParams) };
    if (MIDAS_TOUCHPOINT_PAGES.includes(pathSegment)) {
      filters["touchpoints"] = getShowTouchPoints(searchParams);
    }

    if (!dashboardId) {
      toast.error("An error occurred while downloading CSV file");
      return;
    }

    exportDashboardAnalysis(dashboardId, filters, { page, export: "overall" })
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  // Stays on its old endpoint - the live _dashboard.key for this page's route (/cpo_operational)
  // is "poi_cpo_operational" (confirmed against a real GET /dashboards response -
  // dashboardRegistry.ts's own comment), but ANALYSIS_REGISTRY (backend) is keyed by the literal
  // string "cpo_operational" with no fallback/fuzzy match (services/analysis/service.py's own
  // dashboard.key lookup) - so exporting through the generic endpoint 404s regardless of which
  // real dashboard_id gets resolved. Reverted to the old bespoke endpoint below, confirmed still
  // live/functional.
  const handleCpoOperationalCSVDownload = () => {
    const searchParams = window.location.search;
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 1000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "CPO Operational" });

    postDataV2(`/cpo-operational/get-csv-data`, { ...cpoOperationalParams(searchParams) })
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  // Stays on its old endpoint - the live _dashboard.key for this page's route (/digital_performance)
  // is "online_ce_performance" (confirmed against a real GET /dashboards response -
  // dashboardRegistry.ts's own comment), not "digital_performance" - and ANALYSIS_REGISTRY
  // (backend) is keyed by the literal string "digital_performance" with no fallback/fuzzy match
  // (services/analysis/service.py's own dashboard.key lookup), so there's no single dashboardKey
  // value that satisfies both lookups at once: matching the real row ("online_ce_performance")
  // would 404 against the registry, and matching the registry ("digital_performance") 404s
  // against the row. Reverted to the old bespoke endpoint below, confirmed still live/functional.
  const handleOCECSVDownload = (csv_type: string) => {
    const searchParams = new URLSearchParams(window.location.search);
    const dateRangeVal = searchParams.get("date_range");
    const marketVal = searchParams.get("market")?.split(",");
    const startEndDate = getStartEnd(dateRangeVal);

    searchParams.append("start_date", startEndDate.start);
    searchParams.append("end_date", startEndDate.end);
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "OCE" });

    postDataV2(`/digital-performance/get-${csv_type}-data-csv-download`, { ...oceNewParameter(`?${searchParams.toString()}`) })
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          // Backend no longer caps this at 5 countries (cedar-backend PR #278) - generic message.
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  // Stays on its old endpoint - the live _dashboard.key for this page's route
  // (/cpo_digital_performance) is "poi_cpo_digital_performance" (confirmed against a real
  // GET /dashboards response - dashboardRegistry.ts's own comment), but ANALYSIS_REGISTRY
  // (backend) is keyed by the literal string "cpo_digital_performance" with no fallback/fuzzy
  // match (services/analysis/service.py's own dashboard.key lookup) - so exporting through the
  // generic endpoint 404s regardless of which real dashboard_id gets resolved. Reverted to the
  // old bespoke endpoint below, confirmed still live/functional.
  const handleCPOCSVDownload = (csv_type: string) => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "CPO Online" });

    postDataV2(`/certified-pre-owned/get-${csv_type}-data-csv-download`, { ...getCpoParams() })
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          // Backend no longer caps this at 5 countries (cedar-backend PR #278) - generic message.
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  // Stays on its old endpoint - the live _dashboard.key for this page's route (/voc/website) is
  // "poi_voc_website" (confirmed against a real GET /dashboards response - dashboardRegistry.ts's
  // own comment), but ANALYSIS_REGISTRY (backend) is keyed by the literal string "website" with no
  // fallback/fuzzy match (services/analysis/service.py's own dashboard.key lookup) - so exporting
  // through the generic endpoint 404s ("Dashboard 'poi_voc_website' is not registered for
  // analysis.") regardless of which real dashboard_id gets resolved. Reverted to the old bespoke
  // route below, which is unaffected by this mismatch and still live/functional.
  const handleVOCWebsiteCSVDownload = (csv_type: string) => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    const params: Record<string, any> = { ...getVOCParams() };

    if (csv_type == "top-ten-categories") {
      params["experience_ratings"] = vocCsvExperienceRating;
    }

    if (csv_type == "feedback-comments") {
      params["experience_rating"] =
        !experienceRatingParamValue || experienceRatingParamValue == "All" ? [] : experienceRatingParamValue.split(",");
      params["topics"] = !topicParamValue || topicParamValue == "All" ? [] : topicParamValue.split(",");
      params["touchpoints"] = !touchpointParamValue || touchpointParamValue == "All" ? [] : touchpointParamValue.split(",");
    }

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "VOC" });

    postDataV2(`/voc/${csv_type}-csv-download`, params)
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  // Stays on its old endpoint - the live _dashboard.key for this page's route
  // (/voc/quick_voc_sales) is "poi_voc_quick_voc_sales" (confirmed against a real GET /dashboards
  // response - dashboardRegistry.ts's own comment), but ANALYSIS_REGISTRY (backend) is keyed by
  // the literal string "quick_voc_sales" with no fallback/fuzzy match (services/analysis/
  // service.py's own dashboard.key lookup) - so exporting through the generic endpoint 404s
  // regardless of which real dashboard_id gets resolved. Reverted to the old bespoke endpoint
  // below, confirmed still live/functional - MenuOptions.tsx's own csv_type strings
  // ("overall"/"topical-classification"/"feedback-comments") already match this route's real URL
  // segments 1:1, so no mapping table is needed.
  const handleQuickVocSalesCSVDownload = (csv_type: string) => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    const params: Record<string, any> = { ...getQuickVocSalesParams() };

    if (csv_type == "top-ten-categories") {
      params["experience_ratings"] = vocCsvExperienceRating;
    }

    if (csv_type == "feedback-comments") {
      params["google_ratings"] = !googleRatingParamValue || googleRatingParamValue == "All" ? [] : googleRatingParamValue.split(",");
      params["topics"] = !topicParamValue || topicParamValue == "All" ? [] : topicParamValue.split(",");
    }

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "VOC" });

    postDataV2(`/quick-voc-sales/${csv_type}-csv-download`, params)
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  // Same key-mismatch reasoning as handleQuickVocSalesCSVDownload above - the live _dashboard.key
  // for /voc/quick_voc_after_sales is "poi_voc_quick_voc_after_sales", not "quick_voc_after_sales".
  const handleQuickVocAfterSalesCSVDownload = (csv_type: string) => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    // Was wrongly reusing getQuickVocSalesParams() here (pre-existing bug, unrelated to this
    // migration) - fixed to use the after-sales page's own params while touching this handler.
    const params: Record<string, any> = { ...getQuickVocAfterSalesParams() };

    if (csv_type == "top-ten-categories") {
      params["experience_ratings"] = vocCsvExperienceRating;
    }

    if (csv_type == "feedback-comments") {
      params["google_ratings"] = !googleRatingParamValue || googleRatingParamValue == "All" ? [] : googleRatingParamValue.split(",");
      params["topics"] = !topicParamValue || topicParamValue == "All" ? [] : topicParamValue.split(",");
    }

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "VOC" });

    postDataV2(`/quick-voc-after-sales/${csv_type}-csv-download`, params)
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  // Stays on its old endpoint - the live _dashboard.key for this page's route (/voc/cec_voc) is
  // "poi_voc_cec_voc" (confirmed against a real GET /dashboards response - dashboardRegistry.ts's
  // own comment), but ANALYSIS_REGISTRY (backend) is keyed by the literal string "cec_voc" with no
  // fallback/fuzzy match (services/analysis/service.py's own dashboard.key lookup) - so exporting
  // through the generic endpoint 404s regardless of which real dashboard_id gets resolved.
  // Reverted to the old bespoke endpoint below, confirmed still live/functional.
  const handleQuickVocCecCSVDownload = () => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "VOC" });

    postDataV2(`/quick-voc-cec/csv-download`, getQuickVocCecParams())
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  // handleCustomerJourneyVocCSVDownload and handleEuropeActivationCSVDownload (Europe Activation
  // Excel export) were both removed here - dead code. Neither button's own visibility gate
  // (MenuOptions.tsx, "voc/voc_360" and "europe_activation") ever matched a real route, and
  // ANALYSIS_REGISTRY confirms customer_journey_voc has no export capability at all (filters-only)
  // while europe_activation isn't registered under any key.

  const handleVOCExcelDownload = () => {
    // The filename used to be built here (date_range/market-derived) for the client-side saveAs()
    // call - the backend's own ExportDownloadOut.file_name (via triggerExportDownload) is now the
    // real source of truth for that, so there's nothing left for this handler to compute up front.
    toast.success(
      <div>
        XLSX downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "VOC" });

    postDataV2(`/voc/excel-download`, { ...getVOCParams() })
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else if (res?.status === 422) {
          toast.error("Excel download must have only one market");
        } else {
          toast.error("An error occurred while downloading XLSX file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading XLSX file"));
  };

  const handlePMOCSVDownload = () => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "PMO" });

    if (!dashboardId) {
      toast.error("An error occurred while downloading CSV file");
      return;
    }

    exportDashboardAnalysis(dashboardId, { ...pmoParamsIncludeAvgModels() }, { export: "overall" })
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  // handleMarketingCalendarCSVDownload stays on its old endpoint - product_marketing_optimization
  // only registers a single "overall" export (matching handlePMOCSVDownload above); this one's
  // "marketing activity" CSV has no distinct counterpart in ANALYSIS_REGISTRY yet.
  const handleMarketingCalendarCSVDownload = () => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "PMO" });

    postDataV2(`/product-marketing-optimization/get-marketing-activity-csv-data`, { ...pmoParamsIncludeAvgModels() })
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  // handleDFPCSVDownload, handleProductPerformanceCSVDownload, and handleMSRPCSVDownload
  // (immediately below) all stay on their old bespoke endpoints - confirmed gaps, not migrated:
  // drivers_flash_performance/msrp have no ANALYSIS_REGISTRY entry at all, and the top-level
  // /product_performance dashboard route is confirmed absent from the real /dashboards seed
  // (dashboardRegistry.ts's own comment) even though "product_performance" is a registered
  // analysis key - no dashboard_id is resolvable for this specific route either way.
  const handleDFPCSVDownload = () => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "DFP" });

    postDataV2(`/drivers-for-flash-performance/get-csv-data`, { ...getPmoQueryParams() })
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  const handleProductPerformanceCSVDownload = () => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );
    const searchParams = Object.fromEntries(new URLSearchParams(window.location.search));
    const dataParam = searchParams.date_range.split(" ").join("_").toLocaleLowerCase();

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "Product Performance" });

    postDataV2(`/product-performance/csv-download`, { ...getQueryParamsProductPerformance() })
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  const handleMSRPCSVDownload = () => {
    // The filename used to be built here (date-range-derived) for the client-side saveAs() call -
    // the backend's own ExportDownloadOut.file_name (via triggerExportDownload) is now the real
    // source of truth for that.
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "MSRP" });

    postDataV2(`/msrp/csv-download`, { ...msrpApiParamsToObject() })
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  const handleDMEOCSVDownload = () => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "DMEO" });

    if (!dashboardId) {
      toast.error("An error occurred while downloading CSV file");
      return;
    }

    // dmeo's registry entry hides landing_page from /filters but still accepts it as a DMEOFilters
    // field on /export - getParams() already includes it from the URL, covering both the main and
    // landing-page-performance routes via this one shared "dmeo" export (dropping the old
    // ?page=dmeo|landing_page split, which routed to bespoke endpoints that no longer exist here).
    exportDashboardAnalysis(dashboardId, { ...getParams() }, { export: "overall" })
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  const { metric: journeyExcellenceMetric } = useContext(JourneyExcellenceContext);

  // Stays on its old endpoints - journey_excellence has no ANALYSIS_REGISTRY entry at all.
  const handleJourneyExcellenceCSVDownload = () => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "Journey Excellence" });

    let csvEndpoint = "/journey-excellence/csv-download";

    if (journeyExcellenceMetric == "FMI") {
      csvEndpoint = "/journey-excellence/fmi/csv-download";
    } else if (journeyExcellenceMetric == "Online Journey") {
      csvEndpoint = "/journey-excellence/online-journey/csv-download";
    } else if (journeyExcellenceMetric == "Leads Management System") {
      csvEndpoint = "/journey-excellence/lms/csv-download";
    } else if (journeyExcellenceMetric == "Product Journey") {
      csvEndpoint = "/journey-excellence/product-journey/csv-download";
    }

    postDataV2(`${csvEndpoint}`, { ...journeyExcellenceApiParamsToObject() })
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  // Stays on its old endpoints - the live _dashboard.key for each of these 4 pages'
  // own route (confirmed against a real GET /dashboards response - dashboardRegistry.ts's own
  // comment) never equals the string this handler used to pick an export key by
  // (dashboardKey): /use_ownership/overview's real key is "use_ownership" (not "overview" -
  // ANALYSIS_REGISTRY's own "overview" key is coincidentally the right registry key, just the
  // wrong _dashboard.key lookup value - no single dashboardKey satisfies both at once), and the
  // other 3 are all "poi_use_ownership_*" (not the bare "online_account_and_engagement"/
  // "service_lead"/"self_service" this handler expected). Reverted to the old per-path endpoint
  // selection below, confirmed still live/functional.
  const handleUseOwnershipCSVDownload = () => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "Use Ownership" });

    let csvEndpoint = "/use-ownership/overview/get-csv-download";

    if (location.pathname.includes("online_account_and_engagement")) {
      csvEndpoint = "/use-ownership/online-account-and-engagement/get-csv-download";
    } else if (location.pathname.includes("service_lead")) {
      csvEndpoint = "/use-ownership/service-lead/get-csv-download";
    } else if (location.pathname.includes("self_service")) {
      csvEndpoint = "/use-ownership/self-service/get-csv-download";
    }

    // Was wrongly reusing journeyExcellenceApiParamsToObject() here (pre-existing bug, unrelated
    // to this migration) - fixed to use Use Ownership's own params while touching this handler.
    postDataV2(csvEndpoint, { ...apiParamsToObjectUseOwnership() })
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  const handleBrandPerformanceCSVDownload = () => {
    const searchParams = Object.fromEntries(new URLSearchParams(window.location.search));
    const page = searchParams?.performance_page;
    const nissan_competitor = searchParams?.nissan_competitor;

    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: `${location.pathname}-${page?.replace("_", "-")}`, dashboard: "Brand Performance" });

    const handleResult = (res: any) => {
      if (isExportDownload(res)) {
        triggerExportDownload(res);
      } else if (res?.status === 403) {
        toast.error("You don't have permission to download this file", { autoClose: 10000, pauseOnHover: true });
      } else {
        toast.error("An error occurred while downloading CSV file");
      }
    };
    const handleFailure = () => toast.error("An error occurred while downloading CSV file");

    // 2 of the 22 brand_performance tabs (yearly/RV table-for-download) have no
    // ANALYSIS_REGISTRY page/export yet - brandPerformanceCustomCSVUrl (their old bespoke URL)
    // stays as the fallback for exactly those two.
    if (dashboardId && brandPerformancePage && brandPerformanceExport) {
      exportDashboardAnalysis(
        dashboardId,
        { ...brandPerformanceApiParamsToObject(window.location.search) },
        { page: brandPerformancePage, export: brandPerformanceExport, nissan_competitor: nissan_competitor == "nissan_competitor" }
      )
        .then(handleResult)
        .catch(handleFailure);
    } else if (brandPerformanceCustomCSVUrl) {
      postDataV2(brandPerformanceCustomCSVUrl, { ...brandPerformanceApiParamsToObject(window.location.search) })
        .then(handleResult)
        .catch(handleFailure);
    } else {
      handleFailure();
    }
  };

  const handleBrandPerformanceTableCSVDownload = () => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: `${location.pathname}-purchase-funnel-table`, dashboard: "Brand Performance" });

    if (!dashboardId) {
      toast.error("An error occurred while downloading CSV file");
      return;
    }

    exportDashboardAnalysis(
      dashboardId,
      { ...brandPerformanceApiParamsToObject(window.location.search) },
      { page: "purchase_funnel", export: "awareness_consideration_table" }
    )
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else if (res?.status === 403) {
          toast.error("You don't have permission to download this file", { autoClose: 10000, pauseOnHover: true });
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  // Stays on its old endpoints - ANALYSIS_REGISTRY has plausible-looking "dis"/"product_performance"/
  // "product_performance_deep_dive" entries, but which real dashboard_id each of Executive
  // Summary's 3 sub-pages actually resolves to can't be confirmed without a live /dashboards
  // response (dashboardRegistry.ts's own key strings - "executive_summary" etc. - are a separate,
  // hand-maintained access-control list, not necessarily the real _dashboard.key values the
  // registry is keyed by). Left unmigrated rather than guessed.
  const handleExecutiveSummaryCSVDownload = (isChannelMix?: boolean) => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    let params = {};

    const page = location.pathname?.split("/")?.at(-1)?.replaceAll("_", "-");
    const path = isChannelMix
      ? `/executive-summary/${page}/channel-mix-performance-csv-download`
      : `/executive-summary/${page}/csv-download`;

    if (page == "dis") {
      params = { ...getExecutiveSummaryDisParams() };
      eventTracking(MixpanelEvents.csv_download, { dashboard: "Executive Summary", page: "Digitally Influenced Sales" });
    } else if (page == "product-performance") {
      params = { ...getExecutiveSummaryProductPerformanceParams() };
      eventTracking(MixpanelEvents.csv_download, { dashboard: "Executive Summary", page: "Product Performance" });
    } else if (page == "product-performance-deep-dive") {
      params = { ...getExecutiveSummaryProductPerformanceDeepDiveParams() };
      eventTracking(MixpanelEvents.csv_download, { dashboard: "Executive Summary", page: "Product Performance Deep Dive" });
    }

    postDataV2(path, params)
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  const handleDISCSVDownload = (csvType: string) => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "DIS" });

    // Stays on its old endpoint - the live _dashboard.key for this page's route
    // (/purchase_funnel_report) is "poi_purchase_funnel_report" (confirmed against a real
    // GET /dashboards response - dashboardRegistry.ts's own comment), but ANALYSIS_REGISTRY
    // (backend) is keyed by the literal string "purchase_funnel_report" with no fallback/fuzzy
    // match (services/analysis/service.py's own dashboard.key lookup) - so exporting through the
    // generic endpoint 404s regardless of which real dashboard_id gets resolved. Reverted to the
    // old bespoke endpoints below (both branches), which are unaffected by this mismatch and still
    // live/functional.
    const endpoint = csvType == "car model" ? "/dis/get-sales-by-car-model-csv-download" : "/dis/get-csv-download";

    postDataV2(endpoint, { ...getPurchaseFunnelParameters() })
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  // Stays on its old endpoint - mtm/journey_excellence_market_performance_summary has no
  // ANALYSIS_REGISTRY entry.
  const handlMarketPerformanceSummaryCSVDownload = () => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "Marketing Performance Summary" });

    postDataV2(`/mtm/csv-download`, { ...getMaketingPerformanceSummaryParams() })
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  const handleEcommerceCSVDownload = () => {
    toast.success(
      <div>
        CSV downloading <br />
      </div>,
      { autoClose: 10000, pauseOnHover: true }
    );

    eventTracking(MixpanelEvents.csv_download, { page: location.pathname, dashboard: "Ecommerce" });

    if (!dashboardId) {
      toast.error("An error occurred while downloading CSV file");
      return;
    }

    exportDashboardAnalysis(dashboardId, { ...ecommerceApiParamsToObject() }, { export: "overall" })
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  const { isVisible, setIsVisible, documentation } = useContext(DocumentationContext);

  return (
    <>
      <ErrorBoundary fallback={<ErrorMsg />}>{filter}</ErrorBoundary>

      <DashboardAlertWidget />

      {!hideAppRatingsBanner && (path.includes("ccs_cockpit") || path.includes("app_ratings") || path.includes("ccs_analysis")) && (
        <div
          className={`dashboard_banner hide_on_mobile ${
            showBanner || (path.includes("ccs_cockpit") && !hideAppRatingsBanner) || path.includes("app_ratings") ? "" : "isHidden"
          }`}
          onMouseEnter={() => setBannerHovered(true)}
          onMouseLeave={() => setBannerHovered(false)}
        >
          <p>
            {bannerText}
            <span className={"detail"}> {detailText}</span>
          </p>

          <p className={`${path.includes("ccs_cockpit") || path.includes("app_ratings") ? "" : "isHidden"}`}>
            App data for <span style={{ fontWeight: "bold" }}>Android</span> is not available between December 2021 and March 14th 2022
          </p>

          <p
            className={`${
              (path.includes("ccs_cockpit") || path.includes("ccs_analysis")) && marketParamValue == "United States" ? "" : "isHidden"
            }`}
          >
            NNA has updated their KPI for active users from June &apos;22, YoY comparison is not applicable
          </p>
        </div>
      )}

      {showBanner &&
        (path.includes("cpo_digital_performance") ||
          path.includes("mtm_metrics") ||
          path.includes("msrp") ||
          path.includes("ccs_journey") ||
          path.includes("midas") ||
          path.includes("use_ownership") ||
          path.includes("top_flop") ||
          path.includes("journey_excellence") ||
          path.includes("executive_summary/product_performance") ||
          path.includes("cpo_operational") ||
          path.includes("voc/quick_voc_after_sales") ||
          path.includes("voc/cec_voc") ||
          path.includes("voc/customer_journey_voc") ||
          path.includes("voc/quick_voc_sales") ||
          path.includes("voc/website") ||
          path.includes("ccs_vital_signs/ccs_monetization") ||
          path.includes("brand_performance") ||
          path.includes("ecommerce") ||
          path.includes("ccs_vital_signs/ccs_cockpit")) && (
          <div
            className={`dashboard_banner hide_on_mobile `}
            onMouseEnter={() => setBannerHovered(true)}
            onMouseLeave={() => setBannerHovered(false)}
          >
            <p style={{ whiteSpace: "pre-line" }}>
              {bannerText}
              <span className={"detail"}> {detailText}</span>
            </p>
          </div>
        )}

      <div id="container" onScroll={handleScroll}>
        <div id={"dashboard_items"} className={extraClass}>
          {/*Mobile Filter btn*/}
          <div className="hide_on_desktop filter_float" id="filter_float">
            <MetricBreadcrumb />
            <button
              className={"filter_button"}
              onClick={(evt: React.MouseEvent<HTMLButtonElement>) => {
                evt.preventDefault();
                setShowMobileFilter(true);
              }}
            >
              Filters
            </button>
          </div>
          {pageTitle}
          <MenuOptions
            onHandleOCECSVDownload={handleOCECSVDownload}
            onHandleCPOCSVDownload={handleCPOCSVDownload}
            onHandlePMOCSVDownload={handlePMOCSVDownload}
            onHandleDFPCSVDownload={handleDFPCSVDownload}
            onHandleDMEOCSVDownload={handleDMEOCSVDownload}
            onHandleMarketingCalendarCSVDownload={handleMarketingCalendarCSVDownload}
            onHandleMidasCsvDownload={handleMidasCSVDownload}
            onHandleProductPerformanceCSVDownload={handleProductPerformanceCSVDownload}
            onHandleVOCWebsiteCSVDownload={handleVOCWebsiteCSVDownload}
            onHandleVOCExcelDownload={handleVOCExcelDownload}
            onHandleQuickVocSalesCSVDownload={handleQuickVocSalesCSVDownload}
            onHandleQuickVocAfterSalesCSVDownload={handleQuickVocAfterSalesCSVDownload}
            onHandleQuickVocCecCSVDownload={handleQuickVocCecCSVDownload}
            onHandleJourneyExcellenceCSVDownload={handleJourneyExcellenceCSVDownload}
            onHandleCpoOperationalCSVDownload={handleCpoOperationalCSVDownload}
            onHandleMSRPCSVDownload={handleMSRPCSVDownload}
            onHandleExecutiveSummaryCSVDownload={handleExecutiveSummaryCSVDownload}
            onHandlePDFDownload={() => handlePDFDownload(theme)}
            onHandleSaveHomePageClick={handleSaveHomePageClick}
            onHandleBrandPerformanceCSVDownload={handleBrandPerformanceCSVDownload}
            onHandleBrandPerformanceTableCSVDownload={handleBrandPerformanceTableCSVDownload}
            onHandleUseOwnershipCSVDownload={handleUseOwnershipCSVDownload}
            onHandleMarketingPerformanceSummaryDownload={handlMarketPerformanceSummaryCSVDownload}
            onHandleDISCsvDownload={handleDISCSVDownload}
            onHandleEcommerceCSVDownload={handleEcommerceCSVDownload}
            lastDataRefresh={lastDataRefresh}
            dataDocumentation={dataDocumentation}
            showLastDataRefreshAlert={showLastDataRefreshAlert}
            hideDownloadPdf={hideDownloadPdf}
            dataAvailableTerm={dataAvailableTerm}
          />
          {/*Main content*/}
          <div className={!props.isMTM ? "contentWrapper" : "contentWrapperExtend"}>{children}</div>
        </div>
      </div>

      <Documentation isVisible={isVisible} documentation={documentation} onCloseClick={() => setIsVisible(false)} />
      <SaveHomePage isVisible={showSaveHomePage} onCloseClick={handleCloseSaveHomePage} />
      <ToastContainer
        position="top-right"
        autoClose={2500}
        hideProgressBar={true}
        newestOnTop={true}
        closeOnClick={true}
        draggable={false}
        pauseOnHover={false}
      />
    </>
  );
};
